import {
    MoneyPoolCashin,
    MoneyPoolCashinTypePortion,
    MoneyPoolCashinTypeRepayment,
    MoneyPoolCashoutCustom,
    MoneyPoolCashoutReason,
    MoneyPoolCashoutTypeLoan,
    MoneyPoolTransactionTypes,
} from '../../../../../../utilities/helper/moneyPools'
import { getTransactionStatusText } from '../../../../../../utilities/helper/Funds'
import DeleteTransactionModal from 'components/modals/deleteTransaction'
import HashtagText from 'components/hashtagText'
import TransferTrackingLink from 'components/transferTrackingLink'
import posthog from 'posthog-js'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import close_icon from 'assets/images/rectangle/close.svg'
import { dayKey, describeDay, timeOfDay } from '../../days'
import { DirectionTile, SignedAmount, StatusChip } from '../../parts'
import useEditDescription from './useEditDescription'

interface ITransactionDetails {
    item: IMoneyPoolTransactionResult
    isManager: boolean
    setIsOpen: () => void
    /** tapping a #hashtag filters the transaction list by it */
    onHashtagClick?: (hashtag: string) => void
}

const DESCRIPTION_MAX_LENGTH = 300

function Row({ title, value }: { title: string; value: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-4 px-3 py-2.5">
            <dt className="shrink-0 text-[13px] font-normal text-text-secondary">
                {title}
            </dt>
            <dd className="m-0 min-w-0 break-words text-left text-sm font-bold text-black">
                {value}
            </dd>
        </div>
    )
}

export default function TransactionDetail({
    item,
    isManager,
    setIsOpen,
    onHashtagClick,
}: ITransactionDetails) {
    const navigate = useNavigate()
    const { id } = useParams()
    const [showDeleteTrxModal, setShowDeleteTrxModal] = useState<boolean>(false)

    const isCashinType = item.type.substring(0, 2) === MoneyPoolCashin
    const canDelete = isManager && item.transaction === null

    // Editing a transaction is otherwise disabled — it used to allow changing
    // amounts, which broke the fund's books. The description is the carve-out:
    // it has no accounting meaning, and hashtags in it are only useful if a
    // typo can be corrected.
    const {
        isEditing,
        draft,
        setDraft,
        startEditing,
        cancelEditing,
        save,
        isSaving,
        description,
    } = useEditDescription({
        moneypoolId: Number(id),
        transactionId: item.id,
        isCashin: isCashinType,
        initialTag: item.tag,
    })

    const status = getTransactionStatusText(item)
    const day = describeDay(dayKey(item.jalali_time))
    const time = timeOfDay(item.jalali_time, true)

    const person = item.transaction
        ? item.transaction.receiver_name
        : item.poolship_name
    const namesBothSides =
        item.type === MoneyPoolCashinTypePortion ||
        item.type === MoneyPoolCashinTypeRepayment

    // `\\n` is how the line break in one of the reasons was typed into the
    // table; shown raw it reads as a stray «\n» in the middle of the sentence.
    const reason =
        item.type === MoneyPoolCashoutCustom
            ? (MoneyPoolCashoutReason[item.reason] ?? '').replace(
                  /\s*\\n\s*/g,
                  ' '
              )
            : ''

    // The loan this cash-out paid. The button used to be drawn with no handler
    // at all — it looked like the way to the loan and did nothing.
    const loanId =
        item.type === MoneyPoolCashoutTypeLoan && typeof item.loan === 'number'
            ? item.loan
            : null

    return (
        <div className="pb-6 text-right">
            {/* What happened, in the same marks the ledger row used for it:
                the direction tile, the signed amount, the status chip. */}
            <div className="relative flex flex-col items-center pt-1 text-center">
                <button
                    type="button"
                    aria-label="بستن"
                    className="absolute left-0 top-0 p-1"
                    onClick={() => setIsOpen()}
                >
                    <img className="h-6 w-6" src={close_icon} alt="" />
                </button>
                <DirectionTile cashin={isCashinType} large />
                <p className="mb-0 mt-3 text-sm leading-6 text-text-body">
                    {MoneyPoolTransactionTypes[item.type]}
                </p>
                <div className="mt-0.5 flex">
                    <SignedAmount
                        amount={item.amount}
                        cashin={isCashinType}
                        large
                    />
                </div>
                {status && (
                    <div className="mt-2 flex">
                        <StatusChip status={status} />
                    </div>
                )}
            </div>

            <dl className="mb-0 mt-4 divide-y divide-divider rounded-card border border-[#E4E7EB]">
                {namesBothSides ? (
                    <>
                        <Row title="مربوط به" value={person} />
                        <Row
                            title="توسط"
                            value={
                                item.transaction
                                    ? item.transaction.payer_name
                                    : item.registrar_name
                            }
                        />
                    </>
                ) : (
                    <>
                        <Row
                            // On a cash-in this person is handing money TO the
                            // fund — «افزایش موجودی» called them «دریافت
                            // کننده», which reads as the exact opposite of what
                            // happened. Only a withdrawal has a receiver.
                            title={
                                isCashinType ? 'پرداخت کننده' : 'دریافت کننده'
                            }
                            value={person}
                        />
                        {/* «ثبت‌شده توسط مدیر» says a manager typed it in;
                            this says which one. */}
                        {!item.transaction && item.registrar_name && (
                            <Row
                                title="ثبت‌کننده"
                                value={item.registrar_name}
                            />
                        )}
                    </>
                )}

                <Row
                    title={isCashinType ? 'تاریخ واریز' : 'تاریخ برداشت'}
                    value={day.date ? `${day.title} ${day.date}` : day.title}
                />
                {time && <Row title="ساعت" value={time} />}

                {reason && reason !== description.trim() && (
                    <Row title="بابت" value={reason} />
                )}

                {/* 0 is «never recorded» here, not an empty fund: every loan
                    payout and every typed-in installment carries it, and the
                    sheet was announcing «۰ تومان» under all of them. */}
                {item.balance_after_transaction > 0 && (
                    <Row
                        title="مانده صندوق پس از تراکنش"
                        value={
                            <>
                                {item.balance_after_transaction.toLocaleString()}{' '}
                                <span className="text-[11px] font-normal">
                                    تومان
                                </span>
                            </>
                        }
                    />
                )}
            </dl>

            {/* Every transfer Hamyan made out of موجودی نزد همیان since the
                tracking page launched — in flight or long landed. «واریز شده»
                is exactly when a member says the money never came, and the
                page carries the bank's own کد پیگیری. */}
            {item.transfer && (
                <TransferTrackingLink
                    transfer={item.transfer}
                    className="mt-3"
                    onOpen={() =>
                        posthog.capture(
                            'transaction_transfer_tracking_opened',
                            {
                                moneypool_id: Number(id),
                                stage: item.transfer?.stage,
                                type: item.type,
                                is_manager: isManager,
                            }
                        )
                    }
                />
            )}

            {/* description */}
            <div className="mt-3 rounded-card bg-sunken px-3 py-3">
                <div className="flex items-baseline justify-between gap-2">
                    <p className="mb-0 text-[13px] text-text-secondary">
                        توضیحات
                    </p>
                    {isManager && !isEditing && (
                        <button
                            type="button"
                            className="text-sm font-bold text-blue-1"
                            onClick={() => {
                                // The open half of the edit funnel;
                                // `transaction_description_edited` is the
                                // close. Without this, an abandoned edit is
                                // indistinguishable from one never started.
                                posthog.capture(
                                    'transaction_description_edit_started',
                                    {
                                        moneypool_id: Number(id),
                                        is_cashin: isCashinType,
                                        is_new: description === '',
                                    }
                                )
                                startEditing()
                            }}
                        >
                            {description === '' ? 'افزودن توضیح' : 'ویرایش'}
                        </button>
                    )}
                </div>

                {isEditing ? (
                    <>
                        <textarea
                            aria-label="توضیحات تراکنش"
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            rows={3}
                            placeholder="مثلاً: قبض برق #هزینه_جاری"
                            className="mt-2 w-full resize-none rounded-[8px] border border-stroke bg-white p-2 text-sm text-black outline-none focus:border-green-1 focus:outline-none"
                        />
                        <p className="mb-0 mt-1 text-xs leading-5 text-text-secondary">
                            هر واژه‌ای که با # شروع شود هشتگ می‌شود و با زدن روی
                            آن، تراکنش‌های همان هشتگ را می‌بینید.
                        </p>
                        <div className="mt-2 flex gap-2">
                            <button
                                type="button"
                                disabled={isSaving}
                                onClick={save}
                                className="h-[38px] flex-1 rounded-[10px] bg-green-1 text-sm font-bold text-white disabled:opacity-60"
                            >
                                {isSaving ? 'در حال ذخیره…' : 'ذخیره'}
                            </button>
                            <button
                                type="button"
                                disabled={isSaving}
                                onClick={cancelEditing}
                                className="h-[38px] flex-1 rounded-[10px] border border-stroke bg-white text-sm font-bold text-text-secondary disabled:opacity-60"
                            >
                                انصراف
                            </button>
                        </div>
                    </>
                ) : description === '' ? (
                    <p className="mb-0 mt-1.5 text-sm text-text-muted no-description">
                        توضیحی ثبت نشده است.
                    </p>
                ) : (
                    <HashtagText
                        text={description}
                        onHashtagClick={onHashtagClick}
                        className="mt-1.5 block whitespace-pre-wrap text-sm leading-6 text-black"
                    />
                )}
            </div>

            {/* buttons */}
            <div className="mt-5 flex gap-2">
                {canDelete && (
                    <button
                        type="button"
                        className="h-12 flex-1 rounded-[10px] border border-danger text-sm font-bold text-danger-on"
                        onClick={() => setShowDeleteTrxModal(true)}
                    >
                        حذف تراکنش
                    </button>
                )}

                {loanId !== null ? (
                    <button
                        type="button"
                        className="h-12 flex-1 rounded-[10px] bg-green-1 text-sm font-bold text-white"
                        onClick={() => {
                            posthog.capture('transaction_loan_info_opened', {
                                moneypool_id: Number(id),
                                is_manager: isManager,
                            })
                            navigate(
                                `/fund/financial-history/funds-detail/${id}/${item.poolship}/${loanId}`,
                                { state: { name: item.poolship_name } }
                            )
                        }}
                    >
                        اطلاعات وام
                    </button>
                ) : isManager ? (
                    <button
                        type="button"
                        className="h-12 flex-1 rounded-[10px] bg-green-1 text-sm font-bold text-white"
                        onClick={() =>
                            navigate(
                                `/fund/financial-history/${id}/${item.poolship}`
                            )
                        }
                    >
                        اطلاعات عضو
                    </button>
                ) : (
                    <button
                        type="button"
                        className="h-12 flex-1 rounded-[10px] bg-green-1 text-sm font-bold text-white"
                        onClick={() => setIsOpen()}
                    >
                        بازگشت
                    </button>
                )}
            </div>

            <DeleteTransactionModal
                show={showDeleteTrxModal}
                onHide={() => setShowDeleteTrxModal(false)}
                moneypoolId={Number(id)}
                transactionId={item.id}
                isMoneypoolCashin={isCashinType}
            />
        </div>
    )
}
